import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crosshair, Eye, Radar, RotateCw, Shuffle } from 'lucide-react';
import {
  BATTLESHIP_FLEET,
  BATTLESHIP_SIZE,
  cellLabel,
  deriveBattleship,
  isGuestId,
  replayStates,
  validatePlacement,
  type BattleshipCell,
  type BattleshipMove,
  type BattleshipShot,
  type BattleshipShipId,
  type BattleshipShipPlacement,
  type BattleshipState,
  type GameMeta,
} from '@awwyeah/shared';
import {
  battleshipStatusLine,
  boardView,
  canFire,
  commitMoveFor,
  revealNote,
  newSecret,
  otherIndex,
  pendingResolution,
  pendingReveal,
  placeShip,
  placementView,
  playerIndexOf,
  rotateShipNudged,
  selectedShipCells,
  shotChange,
  sunkLog,
  visiblePlacement,
  type BattleshipCellView,
  type BattleshipSecret,
  type ShotFx,
  type ViewerIndex,
} from './battleshipOps';
import {
  GRID_FIELDS,
  TOKENS,
  fleetSpriteUrl,
  shipSpansFromCells,
  shipSpriteUrl,
  type GridSkin,
} from './navalSkin';
import { playFire, playHit, playSonarPing, playSplash, playSunk } from './battleshipSound';
import { SoundToggle } from './components/SoundToggle';
import { unlockGameAudio } from './synth';
import { GameHeader } from './components/GameHeader';
import { SideLine } from './components/SideLine';
import { ReplayControls, ReplayEntryButtons } from './components/ReplayControls';
import { createGameMeta, nowIso } from './gameOps';
import { initialScrubState, scrubReducer } from './replayState';
import { useBattleshipMoves, useGameSecret } from './useGameMoves';
import { useGamesRegistry } from './useGamesRegistry';
import { useGameRoster } from './useHouseholdRoster';
import { publishRematch } from './rematch';

/** Replay cadence — a shot every ~0.6s reads as a volley rather than a slideshow. */
const REPLAY_TICK_MS = 600;

/** How long the shell is in the air before a fast answer is allowed to land. */
const SHELL_MS = 520;

/**
 * Battleship board (WS-17c) — the hidden-information game, and the first place in this app
 * where what you can see genuinely depends on who you are.
 *
 * **Three viewers, three views**, all from the same public move log:
 *  - **You**: your own fleet (read from `game:<id>:user:<you>`, a doc only your session can
 *    open), plus every shot either side has fired and how it was answered.
 *  - **Your opponent**: the mirror image. They can see your shots and their own fleet, and
 *    at no point can their client read yours — not "the UI hides it", the socket 403s.
 *  - **A spectator**: both grids of shots and results, *no ships at all*, until the game
 *    ends and both players reveal. Then everyone sees everything, including a replay
 *    watched a year later.
 *
 * **The two things this component writes on its own**, both via `battleshipOps`' pure
 * deciders and both deliberately silent:
 *  1. `pendingResolution` — an incoming shot is answered automatically the moment this
 *     board is open on the defender's device. Nobody is ever pushed a "resolve your
 *     incoming fire" nudge; that would be a demand, and demands are forbidden
 *     (`memory/no-demand-mechanics.md`, `apps/server/src/modules/games/notify.ts`). If the
 *     defender never opens the app, the game simply waits, saying nothing.
 *  2. `pendingReveal` — once the game is over, this client publishes its placement (and the
 *     salt behind the hash it committed at ready-up) so the end screen can show both boards.
 *     It reveals after a loss and after a forfeit too; a reveal you only publish when you
 *     won would leave half the end screens blank. Nothing on screen *checks* the hash — the
 *     evidence is recorded, unexamined, by owner call 2026-08-04.
 *
 * A duplicate write from either (two tabs open, a re-render mid-sync) is harmless by
 * construction: `deriveBattleship` drops the second one at replay time, exactly as it drops
 * a duplicated Connect-4 move.
 */
export function BattleshipGame({ meta, userId }: { meta: GameMeta; userId: string }): JSX.Element {
  const navigate = useNavigate();
  const registries = useGamesRegistry();
  const roster = useGameRoster(meta.guestNames);

  const playerIds = meta.playerIds;
  const { movesArray, moves, state } = useBattleshipMoves(meta.id, playerIds);
  const { secret, save } = useGameSecret(meta.id, userId);

  const viewer = playerIndexOf(playerIds, userId);
  const isPlayer = viewer !== -1;
  // Rematch writes a new entry to the `games` registry, the one doc a guest may not open
  // (WS-23); members only.
  const canRematch = isPlayer && !isGuestId(userId);
  const names: [string, string] = [
    roster.getMember(playerIds[0]).name,
    roster.getMember(playerIds[1]).name,
  ];

  /* ---------------------------------------------------------------- replay */

  const derive = useCallback(
    (prefix: readonly BattleshipMove[]) => deriveBattleship(prefix, playerIds),
    [playerIds],
  );
  const frames = useMemo(() => replayStates(moves, derive), [moves, derive]);
  const [scrub, dispatchScrub] = useReducer(scrubReducer, initialScrubState);

  const prevMoveCountRef = useRef(moves.length);
  useEffect(() => {
    if (moves.length > prevMoveCountRef.current) dispatchScrub({ type: 'new-move-arrived' });
    prevMoveCountRef.current = moves.length;
  }, [moves.length]);

  useEffect(() => {
    if (scrub.mode !== 'scrubbing' || !scrub.playing) return undefined;
    const timer = setTimeout(
      () => dispatchScrub({ type: 'tick', maxFrame: frames.length - 1 }),
      REPLAY_TICK_MS,
    );
    return () => clearTimeout(timer);
  }, [scrub, frames.length]);

  const replaying = scrub.mode === 'scrubbing';
  const shown: BattleshipState = replaying ? (frames[scrub.frame]?.state ?? state) : state;

  /* ------------------------------------------------------ shot moments */

  // Watch the shown shot list for one shot going out or one being answered, and give it a
  // moment: a whistle and a falling shell, then a splash, a blast, or a sinking. When the
  // answer arrives faster than the shell could fly (both phones open), it waits its turn.
  const [fx, setFx] = useState<ShotFx | null>(null);
  const [banner, setBanner] = useState<{ text: string; defender: 0 | 1 } | null>(null);
  const prevShotsRef = useRef<readonly BattleshipShot[]>(shown.shots);
  const lastFireAtRef = useRef(0);
  const seqRef = useRef(0);
  // Timers live in a ref, not the effect's cleanup: a re-render that hands us an identical
  // shot list mustn't cancel a splash mid-air or strand the banner on screen.
  const fxTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => () => fxTimersRef.current.forEach(clearTimeout), []);
  const shotsKey = shown.shots
    .map((s) => `${s.cell.row},${s.cell.col}:${s.result ?? '?'}${s.sunkShipId ? '!' : ''}`)
    .join('|');
  useEffect(() => {
    const change = shotChange(prevShotsRef.current, shown.shots);
    prevShotsRef.current = shown.shots;
    if (!change) return;
    const { shot, kind } = change;
    const defender = otherIndex(shot.shooterIndex);
    const timers = fxTimersRef.current;
    const show = (k: ShotFx['kind']): void => {
      seqRef.current += 1;
      const seq = seqRef.current;
      setFx({ defender, row: shot.cell.row, col: shot.cell.col, kind: k, seq });
      timers.push(setTimeout(() => setFx((cur) => (cur?.seq === seq ? null : cur)), 1400));
    };
    const land = (): void => {
      show(kind);
      if (kind === 'miss') playSplash();
      else if (kind === 'hit') playHit();
      else if (kind === 'sunk') {
        playSunk();
        const name = BATTLESHIP_FLEET.find((s) => s.id === shot.sunkShipId)?.name ?? 'ship';
        const text =
          viewer === -1
            ? `${names[shot.shooterIndex]} sank ${names[defender]}'s ${name}!`
            : viewer === shot.shooterIndex
              ? `You sank their ${name}!`
              : `They sank your ${name}`;
        setBanner({ text, defender });
        timers.push(setTimeout(() => setBanner(null), 2200));
      }
    };
    if (kind === 'fire') {
      lastFireAtRef.current = Date.now();
      show('fire');
      playFire();
    } else {
      const wait = SHELL_MS - (Date.now() - lastFireAtRef.current);
      if (wait > 0) timers.push(setTimeout(land, wait));
      else land();
    }
    // Keyed on the shot list's contents; names/viewer only flavour the banner text.
  }, [shotsKey]);

  /* --------------------------------------------------- automatic doc writes */

  // Answer incoming fire. Runs off the *live* state, never the scrubbed one — someone
  // watching the history of their own game must still be answering shots meanwhile.
  const resolution = pendingResolution(state, viewer, secret, userId);
  const resolvedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!resolution) return;
    const key = `${state.awaitingResolution?.shotIndex ?? -1}`;
    if (resolvedRef.current === key) return; // one write per pending shot per session
    resolvedRef.current = key;
    movesArray.push([{ ...resolution, at: nowIso() }]);
  }, [resolution, state.awaitingResolution?.shotIndex, movesArray]);

  // Publish our fleet (and the commitment's salt) once the game is over — win, loss or
  // forfeit — so the end screen and any later replay can show both boards.
  const reveal = pendingReveal(state, viewer, secret, userId);
  const revealedRef = useRef(false);
  useEffect(() => {
    if (!reveal || revealedRef.current) return;
    revealedRef.current = true;
    movesArray.push([{ ...reveal, at: nowIso() }]);
  }, [reveal, movesArray]);

  // Mirror a decided game onto the registry meta so the Apps tab's Finished archive has
  // something to filter on. Players only — a spectator's client never writes anything — and
  // never a guest: `games` is the one doc a guest may never open (WS-23), so a guest winner
  // leaves the stamp to the household member's own client.
  useEffect(() => {
    if (!isPlayer || isGuestId(userId) || meta.status === 'done' || state.winner === null) return;
    const at = nowIso();
    registries.map.set(meta.id, {
      ...meta,
      status: 'done',
      winnerId: playerIds[state.winner],
      finishedAt: at,
      updatedBy: userId,
      updatedAt: at,
    });
  }, [isPlayer, meta, playerIds, registries.map, state.winner, userId]);

  /* ------------------------------------------------------- placement phase */

  const committed = viewer !== -1 && state.commits[viewer] !== null;
  const [draft, setDraft] = useState<BattleshipShipPlacement[] | null>(null);
  const [selectedShip, setSelectedShip] = useState<BattleshipShipId>('carrier');

  // Seed the placement grid from the secret we already saved (a reload mid-setup keeps your
  // fleet) or from a shuffle — most people press Shuffle, so start them there.
  useEffect(() => {
    if (draft !== null || committed || !isPlayer) return;
    setDraft(secret ? secret.placement.ships : newSecret().placement.ships);
  }, [draft, committed, isPlayer, secret]);

  const draftValid = draft !== null && validatePlacement({ ships: draft }).ok;

  // A rotation (or a tap-to-place) that doesn't fit gets a brief wiggle instead of silently
  // doing nothing — "no" needs to be visible, not just absent.
  const [wiggleShip, setWiggleShip] = useState<BattleshipShipId | null>(null);
  const wiggle = (id: BattleshipShipId): void => {
    setWiggleShip(id);
    window.setTimeout(() => setWiggleShip((cur) => (cur === id ? null : cur)), 420);
  };

  const shuffle = (): void => setDraft(newSecret().placement.ships);

  const tapCell = (cell: BattleshipCell): void => {
    if (!draft) return;
    const existing = draft.find((s) => s.shipId === selectedShip);
    // Tapping a ship's own bow rotates it; anywhere else moves it there.
    if (existing && existing.row === cell.row && existing.col === cell.col) {
      const rotated = rotateShipNudged(draft, selectedShip);
      if (rotated) setDraft(rotated);
      else wiggle(selectedShip);
      return;
    }
    const next = placeShip(draft, selectedShip, cell, existing?.orientation ?? 'h');
    if (next) setDraft(next);
    else wiggle(selectedShip);
  };

  const rotateSelected = (): void => {
    if (!draft) return;
    const rotated = rotateShipNudged(draft, selectedShip);
    if (rotated) setDraft(rotated);
    else wiggle(selectedShip);
  };

  // Drag-to-place: the grid computes the candidate cell and hands back the whole updated
  // fleet (or nothing, if the drop wasn't valid) — this component only ever writes state.
  const dragPlace = (shipId: BattleshipShipId, next: BattleshipShipPlacement[] | null): void => {
    setSelectedShip(shipId);
    if (next) setDraft(next);
    else wiggle(shipId);
  };

  const readyUp = (): void => {
    if (!draft || !draftValid || viewer === -1) return;
    const next = { placement: { ships: draft }, salt: secret?.salt ?? newSecret().salt };
    save(next); // the private doc — placement + salt, readable only by us
    movesArray.push([{ ...commitMoveFor(next, userId), at: nowIso() }]); // the public hash
  };

  /* ------------------------------------------------------------ battle phase */

  const [armed, setArmed] = useState<BattleshipCell | null>(null);

  const fire = (cell: BattleshipCell): void => {
    if (replaying || !canFire(state, viewer, cell)) return;
    // Two taps to fire: a fat finger at sea costs a battle, and there is no undo for a shot.
    unlockGameAudio();
    if (!armed || armed.row !== cell.row || armed.col !== cell.col) {
      setArmed(cell);
      playSonarPing();
      return;
    }
    setArmed(null);
    movesArray.push([{ type: 'shot', playerId: userId, cell, at: nowIso() }]);
  };

  const resign = (): void => {
    if (!isPlayer || state.phase === 'done') return;
    movesArray.push([{ type: 'forfeit', playerId: userId, at: nowIso() }]);
  };

  const rematch = (): void => {
    const opponentId = playerIds.find((id) => id !== userId) ?? playerIds[1];
    const fresh = createGameMeta({
      kind: 'battleship',
      starterId: userId,
      starterName: roster.getMember(userId).name,
      opponentId,
      opponentName: roster.getMember(opponentId).name,
    });
    publishRematch(registries.map, fresh, meta);
    navigate(`/games/${fresh.id}`);
  };

  /* --------------------------------------------------------------- render */

  const status = battleshipStatusLine(shown, viewer, names);
  const myTurn = !replaying && state.turn === viewer && state.awaitingResolution === null;
  const maxFrame = frames.length - 1;
  const showPlacement = isPlayer && !committed && shown.phase === 'placing' && !replaying;
  const sunkLines = sunkLog(shown, viewer, names);
  const note = revealNote(shown, names);

  return (
    <div className="flex flex-col gap-4 py-4" data-testid="game-board-page" data-kind="battleship">
      <style>{`
        @keyframes awwyeah-shot-pulse { 0%,100% { opacity: 1 } 50% { opacity: .35 } }
        @keyframes awwyeah-wiggle {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-4px); }
          40% { transform: translateX(3px); }
          60% { transform: translateX(-2px); }
          80% { transform: translateX(1px); }
        }
        @keyframes awwyeah-bs-shake-0 {
          0%, 100% { transform: translate(0, 0); }
          20% { transform: translate(-3px, 1px); }
          40% { transform: translate(3px, -1px); }
          60% { transform: translate(-2px, 1px); }
          80% { transform: translate(1px, 0); }
        }
        @keyframes awwyeah-bs-shake-1 {
          0%, 100% { transform: translate(0, 0); }
          20% { transform: translate(3px, -1px); }
          40% { transform: translate(-3px, 1px); }
          60% { transform: translate(2px, -1px); }
          80% { transform: translate(-1px, 0); }
        }
        @keyframes awwyeah-bs-shimmer { from { background-position: 0% 0%; } to { background-position: 100% 100%; } }
        @keyframes awwyeah-bs-sweep { to { transform: rotate(360deg); } }
        @keyframes awwyeah-bs-shell {
          0% { transform: translateY(-260%) scale(2.2); opacity: 0; }
          25% { opacity: 1; }
          100% { transform: translateY(0) scale(.7); opacity: .9; }
        }
        @keyframes awwyeah-bs-ripple { 0% { transform: scale(.2); opacity: 1; } 100% { transform: scale(1.9); opacity: 0; } }
        @keyframes awwyeah-bs-blast { 0% { transform: scale(.1); opacity: 1; } 60% { opacity: .9; } 100% { transform: scale(1); opacity: 0; } }
        @keyframes awwyeah-bs-spark { 0% { transform: translate(-50%, -50%); opacity: 1; } 100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))); opacity: 0; } }
        @keyframes awwyeah-bs-smoke { 0% { transform: translate(-50%, 0) scale(.6); opacity: 0; } 20% { opacity: .9; } 100% { transform: translate(-30%, -26px) scale(2.2); opacity: 0; } }
        @keyframes awwyeah-bs-banner { 0% { transform: translate(-50%, -50%) scale(.6); opacity: 0; } 12% { transform: translate(-50%, -50%) scale(1.06); opacity: 1; } 85% { opacity: 1; } 100% { transform: translate(-50%, -62%); opacity: 0; } }
        @media (prefers-reduced-motion: reduce) {
          [data-kind="battleship"] * { animation: none !important; }
        }
        @keyframes awwyeah-turn-glow {
          0%, 100% { box-shadow: 0 0 0 2px var(--accent), 0 0 0 2px var(--accent); }
          50% { box-shadow: 0 0 0 2px var(--accent), 0 0 18px 2px var(--accent); }
        }
      `}</style>
      <GameHeader
        title={meta.title}
        status={status}
        spectating={!isPlayer}
        myTurn={myTurn}
        finished={shown.phase === 'done'}
      >
        <ReplayEntryButtons
          canWatchReplay={state.phase === 'done' && !replaying && moves.length > 0}
          canSeeHistory={state.phase !== 'done' && !replaying && state.shots.length > 0}
          onWatchReplay={() => {
            dispatchScrub({ type: 'start-scrub', frame: 0 });
            dispatchScrub({ type: 'play' });
          }}
          onSeeHistory={() => dispatchScrub({ type: 'start-scrub', frame: maxFrame })}
        />
      </GameHeader>

      {isPlayer && (
        <SideLine
          color={roster.getMember(userId).color}
          // Shots on both grids are drawn in the shooter's colour, so this is "your pegs".
          detail="your shots"
          turnText={status}
          turnColor={
            shown.phase !== 'done' && state.turn !== null
              ? roster.getMember(playerIds[state.turn]).color
              : null
          }
          myTurn={myTurn}
        />
      )}

      {replaying && (
        <ReplayControls
          scrub={scrub}
          dispatch={dispatchScrub}
          maxFrame={maxFrame}
          finished={state.phase === 'done'}
          positionLabel={
            scrub.frame === 0 ? 'Before the first shot' : `Move ${scrub.frame} of ${maxFrame}`
          }
        />
      )}

      {showPlacement ? (
        <PlacementPhase
          ships={draft ?? []}
          selectedShip={selectedShip}
          onSelectShip={setSelectedShip}
          onTapCell={tapCell}
          onRotate={rotateSelected}
          onShuffle={shuffle}
          onReady={readyUp}
          ready={draftValid}
          onDragPlace={dragPlace}
          wiggleShip={wiggleShip}
        />
      ) : (
        <BattlePhase
          state={shown}
          viewer={viewer}
          secret={secret}
          names={names}
          armed={armed}
          canFireNow={!replaying && state.phase === 'battle' && state.turn === viewer}
          onFire={fire}
          fx={fx}
          banner={banner}
          colorOf={(index) => roster.getMember(playerIds[index]).color}
        />
      )}

      {sunkLines.length > 0 && (
        <ul className="flex flex-col gap-1" data-testid="sunk-log">
          {sunkLines.map((line, i) => (
            <li key={`${line}-${i}`} className="text-sm text-fg-2">
              {line}
            </li>
          ))}
        </ul>
      )}

      {note && (
        <p
          className="flex items-start gap-1.5 rounded-md border border-line bg-raised p-3 text-sm text-fg-2"
          data-testid="reveal-note"
        >
          <Eye size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{note}</span>
        </p>
      )}

      {state.phase === 'done' && canRematch && !replaying && (
        <button
          type="button"
          data-testid="rematch-button"
          onClick={rematch}
          className="h-12 rounded-md bg-inverse text-base font-semibold text-inverse-fg"
        >
          Rematch
        </button>
      )}

      <div className="flex items-center justify-center gap-2">
        {state.phase === 'battle' && isPlayer && !replaying && (
          <button
            type="button"
            data-testid="resign-button"
            onClick={resign}
            className="h-11 px-3 text-xs font-medium text-fg-3"
          >
            Resign
          </button>
        )}
        {!showPlacement && <SoundToggle />}
      </div>
    </div>
  );
}

/* ========================================================================== */
/* Placement                                                                  */
/* ========================================================================== */

/**
 * Drag a ship to where it goes; drop it and the grid snaps it to the nearest legal cell,
 * tinting the ghost green while it's over a valid spot and red when it isn't. Tap-to-place
 * still works underneath as the fallback that never depends on pointer capture behaving:
 * tap a ship, tap the sea, tap the bow again (or the Rotate button) to turn it. Shuffle is
 * first and largest because it is what most people actually press, and Ready is the only
 * thing that writes anything — nothing about your fleet leaves this device until you
 * confirm, and even then only its hash goes anywhere public.
 */
function PlacementPhase({
  ships,
  selectedShip,
  onSelectShip,
  onTapCell,
  onRotate,
  onShuffle,
  onReady,
  ready,
  onDragPlace,
  wiggleShip,
}: {
  ships: readonly BattleshipShipPlacement[];
  selectedShip: BattleshipShipId;
  onSelectShip: (id: BattleshipShipId) => void;
  onTapCell: (cell: BattleshipCell) => void;
  onRotate: () => void;
  onShuffle: () => void;
  onReady: () => void;
  ready: boolean;
  onDragPlace: (shipId: BattleshipShipId, next: BattleshipShipPlacement[] | null) => void;
  wiggleShip: BattleshipShipId | null;
}): JSX.Element {
  const view = placementView(ships);
  const selectedCells = selectedShipCells(ships, selectedShip);

  return (
    <div className="flex flex-col gap-3" data-testid="placement-phase">
      <p className="text-sm text-fg-2">
        Drag a ship to where it goes, or tap a ship then tap the sea. Tap it again to turn it.
        Nobody — not even the hub — sees where they are; only a fingerprint of the layout goes out
        when you&apos;re ready.
      </p>

      <div className="flex flex-wrap gap-2" data-testid="ship-picker">
        {BATTLESHIP_FLEET.map((ship) => (
          <button
            key={ship.id}
            type="button"
            data-testid="ship-chip"
            data-ship-id={ship.id}
            data-selected={ship.id === selectedShip}
            onClick={() => onSelectShip(ship.id)}
            className={`flex h-11 items-center gap-2 rounded-full border px-3 text-sm font-medium ${
              ship.id === selectedShip
                ? 'border-line-strong bg-inverse text-inverse-fg'
                : 'border-line text-fg-2'
            }`}
          >
            {ship.name}
            <span className="text-xs opacity-70">{ship.length}</span>
          </button>
        ))}
      </div>

      <Grid
        testId="placement-grid"
        skin="ocean"
        view={view}
        showShips
        onTapCell={onTapCell}
        interactive
        highlightCells={selectedCells}
        ships={ships}
        onDragPlace={onDragPlace}
        wiggleShip={wiggleShip}
      />

      <div className="flex gap-2">
        <button
          type="button"
          data-testid="shuffle-button"
          onClick={onShuffle}
          className="flex h-12 flex-1 items-center justify-center gap-2 rounded-md border-2 border-line-strong text-sm font-semibold text-fg"
        >
          <Shuffle size={16} aria-hidden="true" /> Shuffle
        </button>
        <button
          type="button"
          data-testid="rotate-button"
          onClick={onRotate}
          aria-label="Rotate selected ship"
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-line text-fg"
        >
          <RotateCw size={16} aria-hidden="true" />
        </button>
      </div>

      <button
        type="button"
        data-testid="ready-button"
        disabled={!ready}
        onClick={onReady}
        className="h-12 rounded-md bg-inverse text-base font-semibold text-inverse-fg disabled:opacity-40"
      >
        Ready — lock in my fleet
      </button>
    </div>
  );
}

/* ========================================================================== */
/* Battle                                                                     */
/* ========================================================================== */

function BattlePhase({
  state,
  viewer,
  secret,
  names,
  armed,
  canFireNow,
  onFire,
  colorOf,
  fx,
  banner,
}: {
  state: BattleshipState;
  viewer: ViewerIndex;
  secret: BattleshipSecret | null;
  names: [string, string];
  armed: BattleshipCell | null;
  canFireNow: boolean;
  onFire: (cell: BattleshipCell) => void;
  colorOf: (index: 0 | 1) => string;
  fx: ShotFx | null;
  banner: { text: string; defender: 0 | 1 } | null;
}): JSX.Element {
  // Sitting across the table: their waters are the far board (top), your own fleet lies on
  // the table in front of you (bottom, a little smaller and tipped back like it's lying flat).
  // A spectator gets both in player order, since neither is "theirs". Desktop puts them side
  // by side, radar first — the side that gets the your-turn treatment.
  const order: (0 | 1)[] = viewer === -1 ? [0, 1] : [otherIndex(viewer), viewer];
  const turnUiActive = viewer !== -1 && state.phase === 'battle';

  return (
    <div
      className="flex flex-col gap-4 md:grid md:grid-cols-2 md:items-start md:gap-4"
      data-testid="battle-phase"
    >
      {order.map((defenderIndex) => {
        const mine = defenderIndex === viewer;
        const placement = visiblePlacement(state, defenderIndex, viewer, secret);
        const view = boardView(state, defenderIndex, placement);
        const targetable = !mine && canFireNow;
        const label = mine
          ? 'Your fleet'
          : viewer === -1
            ? `${names[defenderIndex]}'s waters`
            : 'Their waters';
        // Only the radar (the board you'd fire on) gets a turn treatment — your own fleet
        // doesn't need to shout at you either way.
        const showTurnBanner = !mine && turnUiActive;
        const yourShot = showTurnBanner && targetable;
        const waitingReason = state.awaitingResolution
          ? 'Waiting for the seas to answer'
          : `${names[defenderIndex]}'s turn`;
        const sunk = state.sunkByShooter[otherIndex(defenderIndex)];

        return (
          <section
            key={defenderIndex}
            className={`relative flex flex-col gap-2 ${mine ? 'mx-auto w-[86%] md:w-full' : ''}`}
            data-side={mine ? 'near' : 'far'}
          >
            <div className="flex items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold text-fg">{label}</h3>
              <span className="text-xs text-fg-3" data-testid="fleet-remaining">
                {BATTLESHIP_FLEET.length - sunk.length} of {BATTLESHIP_FLEET.length} afloat
              </span>
            </div>
            {showTurnBanner && (
              <div
                data-testid="turn-banner"
                data-your-turn={yourShot}
                className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                  yourShot
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-line bg-raised text-fg-3'
                }`}
              >
                <Radar size={16} className={yourShot ? 'animate-pulse' : ''} aria-hidden="true" />
                {yourShot ? 'Your shot — take aim' : waitingReason}
              </div>
            )}
            <div
              className={
                yourShot
                  ? 'rounded-md'
                  : showTurnBanner
                    ? 'rounded-md opacity-70 saturate-50 transition-opacity'
                    : ''
              }
              style={{
                ...(yourShot ? { animation: 'awwyeah-turn-glow 2.2s var(--ease) infinite' } : {}),
                // Your own fleet lies flat on the table in front of you.
                ...(mine
                  ? { transform: 'perspective(900px) rotateX(14deg)', transformOrigin: '50% 100%' }
                  : {}),
              }}
            >
              <Grid
                testId={mine ? 'my-grid' : 'target-grid'}
                skin={mine ? 'ocean' : 'radar'}
                defenderIndex={defenderIndex}
                view={view}
                showShips={placement !== null}
                onTapCell={targetable ? onFire : undefined}
                interactive={targetable}
                armed={targetable ? armed : null}
                shotColor={colorOf(otherIndex(defenderIndex))}
                fx={fx?.defender === defenderIndex ? fx : null}
                sweep={yourShot}
              />
            </div>
            <FleetTracker sunk={sunk} testId={mine ? 'my-fleet-tracker' : 'target-fleet-tracker'} />
            {banner?.defender === defenderIndex && (
              <div
                className="pointer-events-none absolute left-1/2 top-1/2 z-10 whitespace-nowrap rounded-full px-4 py-2 font-display text-lg font-semibold"
                style={{
                  background: 'rgba(12,28,40,.94)',
                  color: '#ffe2a8',
                  border: '1.5px solid rgba(255,226,168,.6)',
                  boxShadow: '0 8px 22px rgba(0,0,0,.4)',
                  animation: 'awwyeah-bs-banner 2200ms ease-out both',
                  transform: 'translate(-50%,-50%)',
                }}
                data-testid="sunk-banner"
                role="status"
              >
                {banner.text}
              </div>
            )}
            {targetable && armed && (
              <button
                type="button"
                data-testid="confirm-fire-button"
                onClick={() => onFire(armed)}
                className="h-12 rounded-md bg-inverse text-base font-semibold text-inverse-fg"
              >
                <Crosshair size={16} className="mr-2 inline" aria-hidden="true" />
                Fire at {cellLabel(armed)}
              </button>
            )}
          </section>
        );
      })}
    </div>
  );
}

/** The classic peg-board fleet card: every ship in this fleet, sunk ones shown as wrecks.
 * Public information only — which ships are down, never where any ship is. */
function FleetTracker({
  sunk,
  testId,
}: {
  sunk: readonly BattleshipShipId[];
  testId: string;
}): JSX.Element {
  return (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={testId}>
      {BATTLESHIP_FLEET.map((ship) => {
        const down = sunk.includes(ship.id);
        return (
          <li
            key={ship.id}
            data-ship={ship.id}
            data-sunk={down}
            className="flex items-center"
            title={`${ship.name}${down ? ' — sunk' : ''}`}
          >
            <img
              src={fleetSpriteUrl(ship.id, ship.length, down)}
              alt={`${ship.name}${down ? ', sunk' : ''}`}
              className="h-3"
              style={{
                width: `${ship.length * 0.75}rem`,
                imageRendering: 'pixelated',
                opacity: down ? 0.4 : 1,
                filter: down ? 'grayscale(.6)' : undefined,
              }}
            />
          </li>
        );
      })}
    </ul>
  );
}

/* ========================================================================== */
/* Grid                                                                       */
/* ========================================================================== */

const COLUMN_LETTERS = Array.from({ length: BATTLESHIP_SIZE }, (_, i) =>
  String.fromCharCode(65 + i),
);

/** Width of the row/column label tracks, in px — a real grid track now, not a flex sibling
 * that has to be trusted to match. See the Grid docblock for why that trust used to fail. */
const LABEL_PX = 18;

interface DragState {
  shipId: BattleshipShipId;
  length: number;
  vertical: boolean;
  /** Which cell along the ship's length was grabbed, so the grip point stays fixed while
   * dragging instead of the ship jumping to put its bow under the pointer. */
  grabOffset: number;
  /** Ghost bow position, already clamped to the board. */
  row: number;
  col: number;
  valid: boolean;
  /** Where the gesture started, in viewport px — a pointerup near here is a tap, not a
   * drag, and defers to `onTapCell` (which is what makes tapping a ship's own bow still
   * rotate it: the sprite sits on top of the button and would otherwise eat the tap). */
  startX: number;
  startY: number;
}

/** Below this many px of movement, a pointer gesture on a hull is a tap, not a drag. */
const DRAG_THRESHOLD_PX = 6;

/**
 * One 10x10 grid. Renders exactly what it is handed: `view` already had the "may this
 * viewer see ships" question answered for it by `visiblePlacement`, so there is no way for
 * a rendering bug here to leak a fleet — the ship ids simply aren't in the props.
 *
 * **Geometry** (the alignment fix): row numbers, column letters and the 10x10 field all
 * live in *one* CSS grid — `LABEL_PX repeat(10, 1fr)` on both axes — instead of the previous
 * three independent layouts (a flex label row, a flex label column, and a CSS-grid field
 * with its own border box) that each rounded fractional pixels differently and drifted apart
 * by a cell or more toward row/column J. A label's track is now the same track a cell sits
 * in, computed once by the same layout pass, so they cannot disagree.
 *
 * **Drag-to-place** (placement only, when `ships`/`onDragPlace` are given): pointer-down on
 * a hull starts a drag; pointer-move recomputes the candidate bow cell from the field's
 * bounding rect and tints the ghost by calling the same `placeShip` validator the tap-to-place
 * path uses, so "would this be legal" is answered identically everywhere. Pointer-up commits
 * through `onDragPlace`, which is given `null` on an invalid drop so the caller can wiggle
 * instead of silently snapping back.
 */
function Grid({
  testId,
  defenderIndex,
  view,
  showShips,
  onTapCell,
  interactive,
  armed = null,
  shotColor,
  highlightCells,
  skin,
  ships,
  onDragPlace,
  wiggleShip,
  fx = null,
  sweep = false,
}: {
  testId: string;
  defenderIndex?: 0 | 1;
  view: BattleshipCellView[][];
  showShips: boolean;
  onTapCell?: (cell: BattleshipCell) => void;
  interactive: boolean;
  armed?: BattleshipCell | null;
  shotColor?: string;
  /** Cell keys to outline — the ship being placed. */
  highlightCells?: ReadonlySet<string>;
  /** Naval art skin: your waters are the blue ocean, theirs the green radar. */
  skin: GridSkin;
  /** Present only on the placement grid — enables drag-to-place. */
  ships?: readonly BattleshipShipPlacement[];
  onDragPlace?: (shipId: BattleshipShipId, next: BattleshipShipPlacement[] | null) => void;
  /** A ship that just failed to rotate or drop — plays a reject-wiggle on its hull. */
  wiggleShip?: BattleshipShipId | null;
  /** A shot landing on this grid right now. */
  fx?: ShotFx | null;
  /** Radar sweep — on the board you're about to fire at. */
  sweep?: boolean;
}): JSX.Element {
  const flatCells = view.flat().map((c) => ({
    row: c.cell.row,
    col: c.cell.col,
    shipId: showShips ? c.shipId : null,
    shipSunk: c.shipSunk,
  }));
  const spans = shipSpansFromCells(flatCells);
  const pct = (n: number): string => `${(n / BATTLESHIP_SIZE) * 100}%`;

  const fieldRef = useRef<HTMLDivElement>(null);
  const [hoverCell, setHoverCell] = useState<BattleshipCell | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const draggable = Boolean(ships && onDragPlace);

  const cellFromPoint = (clientX: number, clientY: number): BattleshipCell | null => {
    const rect = fieldRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return null;
    const cellPx = rect.width / BATTLESHIP_SIZE;
    const col = Math.min(
      BATTLESHIP_SIZE - 1,
      Math.max(0, Math.floor((clientX - rect.left) / cellPx)),
    );
    const row = Math.min(
      BATTLESHIP_SIZE - 1,
      Math.max(0, Math.floor((clientY - rect.top) / cellPx)),
    );
    return { row, col };
  };

  const startDrag = (
    e: ReactPointerEvent<HTMLImageElement>,
    span: { shipId: string; row: number; col: number; length: number; vertical: boolean },
  ): void => {
    if (!draggable) return;
    e.preventDefault();
    const point = cellFromPoint(e.clientX, e.clientY);
    if (!point) return;
    const grabOffset = span.vertical ? point.row - span.row : point.col - span.col;
    setDrag({
      shipId: span.shipId as BattleshipShipId,
      length: span.length,
      vertical: span.vertical,
      grabOffset,
      row: span.row,
      col: span.col,
      valid: true,
      startX: e.clientX,
      startY: e.clientY,
    });
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const moveDrag = (e: ReactPointerEvent<HTMLImageElement>): void => {
    if (!drag) return;
    const point = cellFromPoint(e.clientX, e.clientY);
    if (!point) return;
    const maxRow = drag.vertical ? BATTLESHIP_SIZE - drag.length : BATTLESHIP_SIZE - 1;
    const maxCol = drag.vertical ? BATTLESHIP_SIZE - 1 : BATTLESHIP_SIZE - drag.length;
    const row = Math.min(
      maxRow,
      Math.max(0, drag.vertical ? point.row - drag.grabOffset : point.row),
    );
    const col = Math.min(
      maxCol,
      Math.max(0, drag.vertical ? point.col : point.col - drag.grabOffset),
    );
    const orientation = drag.vertical ? 'v' : 'h';
    const valid = ships ? placeShip(ships, drag.shipId, { row, col }, orientation) !== null : false;
    setDrag((d) => (d ? { ...d, row, col, valid } : d));
  };

  const endDrag = (e: ReactPointerEvent<HTMLImageElement>): void => {
    if (!drag) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    const moved = Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY);
    if (moved < DRAG_THRESHOLD_PX) {
      // Barely moved: treat it as the tap-to-place fallback instead of a drop, so tapping a
      // ship's own bow still rotates it even though the hull sprite is what caught the
      // pointer. Use the pointer-up cell (not the grabbed one) — tapping anywhere on the
      // hull, not just the bow, matches the tap-to-place path's own semantics.
      const point = cellFromPoint(e.clientX, e.clientY);
      if (point) onTapCell?.(point);
    } else if (ships && onDragPlace) {
      const orientation = drag.vertical ? 'v' : 'h';
      const next = placeShip(ships, drag.shipId, { row: drag.row, col: drag.col }, orientation);
      onDragPlace(drag.shipId, next);
    }
    setDrag(null);
  };

  // The crosshair band — armed takes priority over hover (a tap that's waiting for
  // confirmation is a stronger signal than wherever the pointer happens to be resting).
  const crosshair = drag ? { row: drag.row, col: drag.col } : (armed ?? hoverCell);

  return (
    <div
      className="relative"
      data-testid={testId}
      data-defender={defenderIndex}
      style={{
        display: 'grid',
        gridTemplateColumns: `${LABEL_PX}px repeat(${BATTLESHIP_SIZE}, 1fr)`,
        gridTemplateRows: `${LABEL_PX}px repeat(${BATTLESHIP_SIZE}, 1fr)`,
        aspectRatio: '1 / 1',
      }}
    >
      {COLUMN_LETTERS.map((letter, col) => (
        <span
          key={letter}
          data-testid="col-label"
          data-col={col}
          className={`flex items-center justify-center text-[9px] font-medium ${
            crosshair?.col === col ? 'font-bold text-fg' : 'text-fg-3'
          }`}
          style={{ gridColumn: col + 2, gridRow: 1 }}
          aria-hidden="true"
        >
          {letter}
        </span>
      ))}
      {view.map((_, row) => (
        <span
          key={row}
          data-testid="row-label"
          data-row={row}
          className={`flex items-center justify-center text-[9px] font-medium ${
            crosshair?.row === row ? 'font-bold text-fg' : 'text-fg-3'
          }`}
          style={{ gridColumn: 1, gridRow: row + 2 }}
          aria-hidden="true"
        >
          {row + 1}
        </span>
      ))}
      <div
        ref={fieldRef}
        className="relative overflow-hidden rounded-md"
        style={{
          gridColumn: `2 / ${BATTLESHIP_SIZE + 2}`,
          gridRow: `2 / ${BATTLESHIP_SIZE + 2}`,
          boxShadow: 'inset 0 0 0 1px var(--app-line-strong)',
          backgroundImage: `url(${GRID_FIELDS[skin]})`,
          backgroundSize: '100% 100%',
          imageRendering: 'pixelated',
          animation:
            fx && (fx.kind === 'hit' || fx.kind === 'sunk')
              ? `awwyeah-bs-shake-${fx.seq % 2} ${fx.kind === 'sunk' ? 520 : 360}ms ease-out`
              : undefined,
        }}
        onPointerLeave={() => setHoverCell(null)}
      >
        {/* Light dancing on the water — your own sea only, very faint. */}
        {skin === 'ocean' && (
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                'repeating-linear-gradient(115deg, rgba(255,255,255,0) 0 14px, rgba(255,255,255,.05) 18px, rgba(255,255,255,0) 24px), repeating-linear-gradient(65deg, rgba(255,255,255,0) 0 22px, rgba(255,255,255,.035) 26px, rgba(255,255,255,0) 32px)',
              backgroundSize: '200% 200%',
              mixBlendMode: 'screen',
              animation: 'awwyeah-bs-shimmer 9s linear infinite',
            }}
            aria-hidden="true"
          />
        )}
        {/* Radar sweep while it's your shot. */}
        {sweep && (
          <div
            className="pointer-events-none absolute"
            style={{
              inset: '-45%',
              backgroundImage:
                'conic-gradient(from 0deg, rgba(140,255,170,0) 0deg, rgba(140,255,170,0) 300deg, rgba(140,255,170,.32) 358deg, rgba(200,255,210,.6) 360deg)',
              animation: 'awwyeah-bs-sweep 3.2s linear infinite',
              zIndex: 1,
            }}
            aria-hidden="true"
            data-testid="radar-sweep"
          />
        )}
        {/* Crosshair band: highlights the row + column the armed/hovered cell sits in — "which
            row am I about to hit", answerable at a glance instead of counting cells. */}
        {crosshair && (
          <>
            <div
              className="pointer-events-none absolute inset-x-0 bg-fg/10"
              style={{ top: pct(crosshair.row), height: pct(1) }}
            />
            <div
              className="pointer-events-none absolute inset-y-0 bg-fg/10"
              style={{ left: pct(crosshair.col), width: pct(1) }}
            />
          </>
        )}

        {/* Ship hulls: sprite per span, under the shot layer. The dragged hull follows the
            pointer at its grabbed offset; a tinted ghost rect under it — green while the drop
            would be legal, red while it wouldn't — is the "valid/invalid tinting" the drag
            asked for, since filtering the sprite's own transparent PNG can't carry color. */}
        {spans.map((span) => {
          const isDraggingThis = drag?.shipId === span.shipId;
          const row = isDraggingThis ? drag.row : span.row;
          const col = isDraggingThis ? drag.col : span.col;
          const wiggling = wiggleShip === span.shipId && !isDraggingThis;
          return (
            <img
              key={span.shipId}
              src={shipSpriteUrl(span, spans)}
              alt=""
              data-testid="ship-sprite"
              data-ship={span.shipId}
              data-sunk={span.sunk}
              data-dragging={isDraggingThis}
              className={draggable ? 'absolute touch-none' : 'pointer-events-none absolute'}
              onPointerDown={draggable ? (e) => startDrag(e, span) : undefined}
              onPointerMove={draggable ? moveDrag : undefined}
              onPointerUp={draggable ? endDrag : undefined}
              onPointerCancel={draggable ? endDrag : undefined}
              style={{
                left: pct(col),
                top: pct(row),
                width: pct(span.vertical ? 1 : span.length),
                height: pct(span.vertical ? span.length : 1),
                imageRendering: 'pixelated',
                opacity: isDraggingThis ? 0.95 : span.sunk ? 0.9 : 1,
                cursor: draggable ? 'grab' : undefined,
                zIndex: isDraggingThis ? 3 : 1,
                animation: wiggling ? 'awwyeah-wiggle 0.42s var(--ease)' : undefined,
              }}
            />
          );
        })}
        {spans
          .filter((span) => span.sunk)
          .map((span) => (
            <div
              key={`smoke-${span.shipId}`}
              className="pointer-events-none absolute"
              style={{
                left: pct(span.col + (span.vertical ? 0.5 : span.length / 2)),
                top: pct(span.row + (span.vertical ? span.length / 2 : 0.5)),
                zIndex: 2,
              }}
              aria-hidden="true"
              data-testid="wreck-smoke"
            >
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="absolute h-3 w-3 rounded-full"
                  style={{
                    left: (i - 1) * 7,
                    background: 'radial-gradient(circle, rgba(70,70,70,.75), rgba(70,70,70,0) 70%)',
                    animation: `awwyeah-bs-smoke 2.6s ease-out ${i * 0.8}s infinite both`,
                  }}
                />
              ))}
            </div>
          ))}
        {fx && (
          <ShotEffect key={fx.seq} fx={fx} left={pct(fx.col)} top={pct(fx.row)} size={pct(1)} />
        )}
        {drag && (
          <div
            data-testid="drag-ghost"
            data-valid={drag.valid}
            className={`pointer-events-none absolute rounded-sm border-2 ${
              drag.valid ? 'border-sync-ok bg-sync-ok/25' : 'border-danger bg-danger/25'
            }`}
            style={{
              left: pct(drag.col),
              top: pct(drag.row),
              width: pct(drag.vertical ? 1 : drag.length),
              height: pct(drag.vertical ? drag.length : 1),
              zIndex: 2,
            }}
          />
        )}

        <div
          className="absolute inset-0 grid"
          style={{ gridTemplateColumns: `repeat(${BATTLESHIP_SIZE}, 1fr)` }}
        >
          {view.flat().map((cellView) => {
            const key = `${cellView.cell.row},${cellView.cell.col}`;
            const isArmed = armed?.row === cellView.cell.row && armed?.col === cellView.cell.col;
            const isHighlighted = highlightCells?.has(key) ?? false;
            const hasShip = showShips && cellView.shipId !== null;
            return (
              <button
                key={key}
                type="button"
                data-testid="battleship-cell"
                data-cell={cellLabel(cellView.cell)}
                data-row={cellView.cell.row}
                data-col={cellView.cell.col}
                data-shot={cellView.shot ?? ''}
                data-ship={hasShip ? cellView.shipId : ''}
                data-sunk={cellView.shipSunk}
                data-armed={isArmed}
                aria-label={`${cellLabel(cellView.cell)}${cellView.shot ? ` — ${cellView.shot}` : ''}`}
                disabled={!interactive}
                onClick={() => onTapCell?.(cellView.cell)}
                onPointerEnter={() => interactive && setHoverCell(cellView.cell)}
                className={`relative flex items-center justify-center text-[11px] font-bold leading-none disabled:cursor-default ${
                  isArmed ? 'ring-2 ring-inset ring-fg' : ''
                } ${isHighlighted ? 'ring-1 ring-inset ring-fg' : ''}`}
                style={{
                  color: shotColor,
                  animation:
                    cellView.shot === 'pending'
                      ? 'awwyeah-shot-pulse 1.4s var(--ease) infinite'
                      : undefined,
                }}
              >
                {cellView.shot === 'hit' && (
                  <img
                    src={TOKENS.hit}
                    alt=""
                    className="h-4/5 w-4/5"
                    style={{ imageRendering: 'pixelated' }}
                  />
                )}
                {cellView.shot === 'miss' && (
                  <img
                    src={TOKENS.miss}
                    alt=""
                    className="h-3/5 w-3/5 opacity-90"
                    style={{ imageRendering: 'pixelated' }}
                  />
                )}
                {cellView.shot === 'pending' ? '◦' : ''}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** The moment a shot lands: a shell coming down, a splash ring, or a fireball. Pure CSS,
 * drawn over one cell; the grid underneath already holds the final token. */
function ShotEffect({
  fx,
  left,
  top,
  size,
}: {
  fx: ShotFx;
  left: string;
  top: string;
  size: string;
}): JSX.Element {
  return (
    <div
      className="pointer-events-none absolute"
      style={{ left, top, width: size, height: size, zIndex: 4 }}
      data-testid="shot-fx"
      data-kind={fx.kind}
      aria-hidden="true"
    >
      {fx.kind === 'fire' && (
        <span
          className="absolute inset-[22%] rounded-full"
          style={{
            background: 'radial-gradient(circle at 35% 30%, #9aa3ad, #2a3038 70%)',
            boxShadow: '0 0 0 2px rgba(255,255,255,.5)',
            animation: 'awwyeah-bs-shell 520ms cubic-bezier(.5,0,1,1) both',
          }}
        />
      )}
      {fx.kind === 'miss' &&
        [0, 1, 2].map((i) => (
          <span
            key={i}
            className="absolute inset-0 rounded-full border-2"
            style={{
              borderColor: 'rgba(235,250,255,.95)',
              animation: `awwyeah-bs-ripple 900ms ease-out ${i * 140}ms both`,
            }}
          />
        ))}
      {(fx.kind === 'hit' || fx.kind === 'sunk') && (
        <>
          <span
            className="absolute rounded-full"
            style={{
              inset: fx.kind === 'sunk' ? '-120%' : '-70%',
              background:
                'radial-gradient(circle, rgba(255,250,210,1) 0 12%, rgba(255,190,60,.95) 28%, rgba(240,80,20,.75) 48%, rgba(120,30,10,0) 70%)',
              animation: `awwyeah-bs-blast ${fx.kind === 'sunk' ? 900 : 650}ms ease-out both`,
            }}
          />
          {Array.from({ length: 6 }, (_, i) => (
            <span
              key={i}
              className="absolute left-1/2 top-1/2 h-1 w-1 rounded-full"
              style={{
                background: '#ffd27a',
                ['--dx' as string]: `${Math.round(Math.cos((i / 6) * Math.PI * 2) * 22)}px`,
                ['--dy' as string]: `${Math.round(Math.sin((i / 6) * Math.PI * 2) * 22)}px`,
                animation: 'awwyeah-bs-spark 600ms ease-out both',
              }}
            />
          ))}
        </>
      )}
    </div>
  );
}
